import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown } from 'lucide-react';
import axios from 'axios';

const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--glass-border)',
    background: 'rgba(255,255,255,0.6)', outline: 'none',
    fontSize: '14px', boxSizing: 'border-box'
};

const Signup = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [showInvite, setShowInvite] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { signup, signInWithGoogle, setUserRole } = useAuth();
    const navigate = useNavigate();

    const handleGoogle = async () => {
        try {
            setError(''); setLoading(true);
            await signInWithGoogle();
            navigate('/dashboard');
        } catch (err) {
            setError('Google sign-up failed. Please try again.');
        } finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setError(''); setLoading(true);
            const userCredential = await signup(email, password, name);
            const user = userCredential.user;
            const apiUrl = process.env.REACT_APP_API_URL;
            const res = await axios.post(`${apiUrl}/api/users/register`, {
                uid: user.uid, email, name, inviteCode
            });
            setUserRole(res.data.role);
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to create account: ' + err.message);
        } finally { setLoading(false); }
    };

    return (
        <div className="container" style={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div className="glass-card" style={{ maxWidth: '380px', width: '100%', padding: '28px' }}>

                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '22px', marginBottom: '4px' }}>Join The Reef</h2>
                    <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>Start your learning journey today</p>
                </div>

                {error && (
                    <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid #e74c3c', color: '#e74c3c', padding: '8px 12px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <AlertCircle size={14} /> {error}
                    </div>
                )}

                {/* Google */}
                <button
                    onClick={handleGoogle}
                    disabled={loading}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '16px' }}
                >
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 4.8C9.7 39.7 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.5-2.6 4.6-4.8 6l6.2 5.2C40.5 35.6 44 30.3 44 24c0-1.3-.1-2.7-.4-4z"/></svg>
                    Continue with Google
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Full Name</label>
                        <input type="text" required style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Kwame Mensah" />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Email</label>
                        <input type="email" required style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Password</label>
                        <input type="password" required minLength={6} style={inputStyle} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" />
                    </div>

                    {/* Admin invite code — collapsed by default */}
                    <button type="button" onClick={() => setShowInvite(!showInvite)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-light)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, marginBottom: showInvite ? '10px' : '16px' }}>
                        <ChevronDown size={13} style={{ transform: showInvite ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        Have an admin invite code?
                    </button>

                    {showInvite && (
                        <div style={{ marginBottom: '16px' }}>
                            <input type="text" style={inputStyle} value={inviteCode} onChange={e => setInviteCode(e.target.value)} placeholder="Enter invite code" />
                        </div>
                    )}

                    <button disabled={loading} className="btn btn-primary" style={{ width: '100%', padding: '10px', fontSize: '14px' }}>
                        {loading ? 'Creating account...' : 'Create Account'}
                    </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--text-light)' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: '500' }}>Log In</Link>
                </p>
            </div>
        </div>
    );
};

export default Signup;
